// Marker management module for Leaflet

import {
    POPUP_SETTINGS,
    MARKER_TAG_SETTINGS,
    MARKER_POPUP_DISPLAY,
    MARKER_HOVER_DISPLAY,
    TAG_SEARCH_SETTINGS,
    LOGGING,
    MARKER_SIZE,
    MARKER_STYLE,
    SPOT_DISPLAY_MODE,
    MARKER_POPUP_CREDITS,
    DOMAIN_NAME,
    MARKER_POPUP_ACTION_BUTTONS
} from '/settings.js';
import { getCurrentLanguage } from '../translations/translations.js';
import { getDistanceMeters, formatDistance } from '../utils/location-utils.js';
import { MarkerPopup } from './MarkerPopup.js';
import { isFavorite, toggleFavorite } from '../utils/favorites.js';
import { DATA_PATHS } from '../dataPaths.js';
import { createIconMarkup } from '../utils/icon.js';

import {
    sanitizeInput,
    isForbiddenChar,
    sanitizePaste
} from '../utils/sanitize-utils.js';

const scale = MARKER_SIZE.SCALE;
const markerStyle = MARKER_STYLE.TYPE === 'circle' ? MARKER_STYLE.CIRCLE : MARKER_STYLE.PIN;
const isCardsMode = SPOT_DISPLAY_MODE === 'CARDS';
const isImagePopupMode = SPOT_DISPLAY_MODE === 'POPUPS_IMAGE';
const shouldHighlightMarker = isCardsMode || isImagePopupMode;
const FOCUSED_Z_OFFSET = 10000; // bring focused marker above others

// Set CSS variables for marker size based on style
if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--marker-width', `${markerStyle.width * scale}px`);
    document.documentElement.style.setProperty('--marker-height', `${markerStyle.height * scale}px`);
    document.documentElement.style.setProperty('--marker-circle', `${markerStyle.circle * scale}px`);
    document.documentElement.style.setProperty('--marker-icon', `${markerStyle.icon * scale}px`);
    document.documentElement.style.setProperty('--marker-style', MARKER_STYLE.TYPE);
}

// --- Global hover control: disable all hovers while any popup is open ---
let isAnyPopupOpen = false;
let focusedSpotId = null; // Track which spot is currently displayed in cards
let markerRegistry = new Map(); // Store markers by spot ID for efficient lookups
let isZooming = false; // Track zoom/pan operations to prevent focus clearing during map movement
let activePopupMarker = null; // Track which marker has popup open (for auto-reopen after zoom)
let popupClosedDuringZoom = false; // Track if popup was actually closed during zoom

function hideAllMarkerHoverTooltips(map) {
    if (!map || !map.eachLayer) return;
    map.eachLayer(layer => {
        if (layer.closeTooltip) {
            try { layer.closeTooltip(); } catch (e) { }
        }
        if (layer.getTooltip && layer.getTooltip()) {
            const tt = layer.getTooltip();
            if (tt._container) tt._container.style.display = 'none';
        }
    });
}
function showAllMarkerHoverTooltips(map) {
    if (!map || !map.eachLayer) return;
    map.eachLayer(layer => {
        if (layer.getTooltip && layer.getTooltip()) {
            const tt = layer.getTooltip();
            if (tt._container) tt._container.style.display = '';
        }
    });
}

// --- Highlight focused marker in CARDS mode ---
let mapZoomListenerAttached = false;
let lastReapplyTime = 0; // Track last reapply to prevent duplicates
let areListenersPaused = false; // Pause listeners during UI transitions

// Listen for spotCleared event to clear marker focus (ONE source of truth)
if (typeof window !== 'undefined') {
    window.addEventListener('spotCleared', () => {
        clearFocusedMarker();
    });
}

// Internal function: just does the focusing without recursion or listener setup
function applyMarkerFocus(spotId) {
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log(`[MARKER] Looking for marker with spot ID: ${spotId}`);
    }

    // First, clear all focused markers efficiently
    markerRegistry.forEach((marker, id) => {
        const iconEl = marker._icon;
        if (iconEl) {
            iconEl.classList.remove('marker-focused');
        }
        if (marker && typeof marker.setZIndexOffset === 'function') {
            try { marker.setZIndexOffset(0); } catch (e) { }
        }
    });

    // Then, focus the specific marker
    const targetMarker = markerRegistry.get(String(spotId));
    if (targetMarker && targetMarker._icon) {
        targetMarker._icon.classList.add('marker-focused');
        if (typeof targetMarker.setZIndexOffset === 'function') {
            try { targetMarker.setZIndexOffset(FOCUSED_Z_OFFSET); } catch (e) { }
        }
        if (window.LOGGING && window.LOGGING.ENABLED) {
            console.log(`[MARKER] ✓ Successfully focused spot ${spotId}`);
        }
    } else {
        if (window.LOGGING && window.LOGGING.ENABLED) {
            console.warn(`[MARKER] ✗ No marker found for spot ID: ${spotId}`);
        }
    }
}

/**
 * Pause marker focus listeners during UI transitions
 * Call this when cards banner is transitioning to prevent unwanted refocusing
 */
export function pauseMarkerListeners() {
    areListenersPaused = true;
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log(`[MARKER] Paused zoom/pan listeners during transition`);
    }
}

/**
 * Resume marker focus listeners after UI transitions
 * Call this when cards banner transition is complete
 */
export function resumeMarkerListeners() {
    areListenersPaused = false;
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log(`[MARKER] Resumed zoom/pan listeners after transition`);
    }
}

export function setFocusedMarker(spotId, map) {
    if (!shouldHighlightMarker) {
        if (window.LOGGING && window.LOGGING.ENABLED) {
            console.log(`[MARKER] Not in highlight mode (CARDS or POPUPS_IMAGE), skipping`);
        }
        return;
    }

    focusedSpotId = spotId;

    if (!map || !map.eachLayer) {
        if (window.LOGGING && window.LOGGING.ENABLED) {
            console.warn(`[MARKER] Map or map.eachLayer not available`);
        }
        return;
    }

    // Attach zoom/pan listeners only once to preserve focus through zoom/pan
    if (!mapZoomListenerAttached && map) {
        // Set flag when zoom/pan starts to prevent focus clearing during movement
        map.on('zoomstart', () => {
            isZooming = true;
            if (window.LOGGING && window.LOGGING.ENABLED) {
                console.log(`[MARKER] Zoom/pan started, preserving focus`);
            }
        });
        map.on('movestart', () => {
            isZooming = true;
        });

        const reapplyFocus = () => {
            // Clear zoom flag when movement ends
            isZooming = false;

            // Reopen popup ONLY if it was closed during zoom
            if (popupClosedDuringZoom && activePopupMarker && activePopupMarker._map) {
                popupClosedDuringZoom = false; // Reset flag
                // Use requestAnimationFrame to reopen at the end of the current render cycle
                requestAnimationFrame(() => {
                    if (activePopupMarker && activePopupMarker._map) {
                        activePopupMarker.openPopup();
                        if (window.LOGGING && window.LOGGING.ENABLED) {
                            console.log(`[MARKER] Reopened popup after zoom`);
                        }
                    }
                });
            }

            // Skip reapply if listeners are paused (during UI transitions)
            if (areListenersPaused) {
                if (window.LOGGING && window.LOGGING.ENABLED) {
                    console.log(`[MARKER] Listeners paused, skipping reapply`);
                }
                return;
            }

            if (focusedSpotId) {
                // Prevent duplicate reapplies within 300ms (both zoomend and moveend fire)
                const now = Date.now();
                if (now - lastReapplyTime < 300) {
                    return; // Skip duplicate reapply
                }
                lastReapplyTime = now;

                if (window.LOGGING && window.LOGGING.ENABLED) {
                    console.log(`[MARKER] Reapplying focus after zoom/pan for spot ${focusedSpotId}`);
                }
                applyMarkerFocus(focusedSpotId);
            }
        };
        map.on('zoomend', reapplyFocus);
        map.on('moveend', reapplyFocus);
        mapZoomListenerAttached = true;
        if (window.LOGGING && window.LOGGING.ENABLED) {
            console.log(`[MARKER] Attached zoom/pan listeners to map`);
        }
    }

    // Apply the focus
    applyMarkerFocus(spotId);
}

// --- Clear focused marker highlighting ---
export function clearFocusedMarker() {
    if (!shouldHighlightMarker) return;

    focusedSpotId = null;
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log(`[MARKER] Clearing all focused markers`);
    }

    // Clear focused class from all markers in registry
    markerRegistry.forEach((marker) => {
        if (marker._icon && marker._icon.classList) {
            marker._icon.classList.remove('marker-focused');
        }
        if (marker && typeof marker.setZIndexOffset === 'function') {
            try { marker.setZIndexOffset(0); } catch (e) { }
        }
    });
}

// --- Clear marker registry ---
export function clearMarkerRegistry() {
    markerRegistry.clear();
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log(`[MARKER] Cleared marker registry`);
    }
}

let tagTranslations = null;
async function loadTagTranslations() {
    if (tagTranslations) return tagTranslations;
    try {
        const response = await fetch('src/translations/translations-tags.json');
        tagTranslations = await response.json();
        return tagTranslations;
    } catch (e) {
        tagTranslations = {};
        return tagTranslations;
    }
}

function getTagTranslation(tag, lang, translations) {
    if (translations && translations[lang] && translations[lang][tag]) {
        return translations[lang][tag];
    }
    return tag.replace(/_/g, ' ');
}

// Helper to ensure icon filename ends with .svg
function getIconFilename(icon) {
    if (!icon) return '';
    return icon.endsWith('.svg') ? icon : icon + '.svg';
}

// Create minimal image-only popup for POPUPS_IMAGE mode
export function createImageOnlyPopup(spot) {
    // Get first image
    const firstImage = spot.images && spot.images.length > 0 ? spot.images[0] : null;

    if (!firstImage) {
        // No image available - return null to prevent popup from showing
        return null;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'marker-popup-image-only';
    import('../utils/image-utils.js').then(({ getCardImagePaths }) => {
        import('./MarkerPopupCarrouselLightbox.js').then(({ openLightbox }) => {
            const paths = getCardImagePaths(firstImage.id, 'webp');

            // Create picture element
            const picture = document.createElement('picture');
            picture.className = 'marker-popup-image-picture';

            const source = document.createElement('source');
            source.srcset = paths.webp;
            source.type = 'image/webp';
            picture.appendChild(source);

            const img = document.createElement('img');
            img.src = paths.webp;
            img.alt = firstImage.alt_text || '';
            img.loading = 'eager';
            img.style.cursor = 'pointer';
            picture.appendChild(img);

            // Click handler to open lightbox with all images
            picture.addEventListener('click', (e) => {
                e.stopPropagation();
                openLightbox({ images: spot.images, initialIndex: 0 });
            });

            wrapper.appendChild(picture);

            // Add "+N more images" badge if there are multiple images
            if (spot.images && spot.images.length > 1) {
                const moreBadge = document.createElement('div');
                moreBadge.className = 'marker-popup-image-more-badge';
                moreBadge.textContent = `+${spot.images.length - 1}`;
                moreBadge.style.cursor = 'pointer';
                moreBadge.addEventListener('click', (e) => {
                    e.stopPropagation();
                    openLightbox({ images: spot.images, initialIndex: 0 });
                });
                wrapper.appendChild(moreBadge);
            }

            // Add copyright overlay if exists AND respects MARKER_POPUP_CREDITS settings
            if (firstImage.copyright && firstImage.copyright.trim()) {
                // Check if this is "our" credit (domain name) or external photographer
                const isOurCredit = firstImage.copyright === DOMAIN_NAME || firstImage.copyright.toLowerCase().includes(DOMAIN_NAME.toLowerCase());
                const shouldShow = isOurCredit ? MARKER_POPUP_CREDITS.our_credits : MARKER_POPUP_CREDITS.general_credits;

                if (shouldShow && firstImage.copyright_url) {
                    import('../utils/icon.js').then(({ createIconElement }) => {
                        const copyrightDiv = document.createElement('div');
                        copyrightDiv.className = 'marker-popup-image-copyright';
                        copyrightDiv.style.cursor = 'pointer';

                        const cameraIcon = createIconElement('camera-solid', 'marker-popup-image-copyright-icon');
                        copyrightDiv.appendChild(cameraIcon);

                        if (firstImage.copyright_url) {
                            const link = document.createElement('a');
                            link.href = firstImage.copyright_url;
                            link.target = '_blank';
                            link.rel = 'noopener noreferrer';
                            link.textContent = firstImage.copyright;
                            link.addEventListener('click', (e) => {
                                e.stopPropagation(); // Let link work normally
                            });
                            copyrightDiv.appendChild(link);
                        } else {
                            copyrightDiv.appendChild(document.createTextNode(firstImage.copyright));
                        }

                        // Clicking copyright (but not link) also opens lightbox
                        copyrightDiv.addEventListener('click', (e) => {
                            if (e.target.tagName !== 'A') {
                                e.stopPropagation();
                                openLightbox({ images: spot.images, initialIndex: 0 });
                            }
                        });

                        wrapper.appendChild(copyrightDiv);
                    });
                }
            }
        });
    });

    return wrapper;
}

export function createMarkerPopup(spot, searchedTags = [], matchType = 'partial') {
    // Get current language for fallback logic
    const lang = getCurrentLanguage();
    const langSuffix = lang === 'es' ? 'es' : lang === 'de' ? 'de' : 'en';

    // Use processed fields with fallback to English if empty
    let title = spot.title || '';
    let description = spot.description || '';

    // Fallback to English if current language field is empty
    if (!title) {
        const nameField = `name_${langSuffix}`;
        title = spot[nameField] || spot.name_en || '';
    }
    if (!description) {
        const descField = `description_${langSuffix}`;
        description = spot[descField] || spot.description_en || '';
    }

    const showTags = (Array.isArray(spot.tags) ? [...spot.tags] : []);

    // Distance logic
    let distance = '';
    if (MARKER_POPUP_DISPLAY.distance && typeof window !== 'undefined') {
        const userLoc = window.userLocation;
        if (userLoc && spot.coordinates) {
            const { latitude: userLat, longitude: userLng } = userLoc;
            const { latitude: spotLat, longitude: spotLng } = spot.coordinates;
            if (typeof userLat === 'number' && typeof userLng === 'number' && typeof spotLat === 'number' && typeof spotLng === 'number') {
                const meters = getDistanceMeters(userLat, userLng, spotLat, spotLng);
                distance = formatDistance(meters);
            }
        }
    }

    const popupNode = MarkerPopup({
        title,
        description,
        tags: showTags,
        distance,
        spot,
        searchedTags
    });
    return popupNode;
}

export function createSpotMarker(spot, searchedTags = [], matchType = 'partial') {
    // Fallback: Ignore spots with invalid coordinates
    if (!spot.coordinates ||
        typeof spot.coordinates.latitude !== 'number' ||
        typeof spot.coordinates.longitude !== 'number' ||
        isNaN(spot.coordinates.latitude) ||
        isNaN(spot.coordinates.longitude)) {
        if (LOGGING && LOGGING.marker) {
            console.warn('[MARKER] Skipping spot with invalid coordinates:', spot);
        }
        return null;
    }
    const iconClass = spot.icon || 'fa-map-marker-alt'; // Fallback icon
    // If spot is missing valid coordinates, skip it (do not create marker)
    // Always return null for invalid coordinates so caller can skip

    // Apply per-spot size multiplier (clamped between 0.5 and 3.0)
    const spotSize = spot.size ? Math.max(0.5, Math.min(3.0, parseFloat(spot.size) || 1.0)) : 1.0;
    const finalScale = scale * spotSize;

    const iconSize = [markerStyle.width * finalScale, markerStyle.height * finalScale];
    const iconAnchor = [markerStyle.anchor[0] * finalScale, markerStyle.anchor[1] * finalScale];
    const popupAnchor = [
        markerStyle.popupAnchor[0] * finalScale,
        (markerStyle.popupAnchor[1] * finalScale) - 8 // Nudge popup 8px higher to account for focused marker scaling
    ];

    const iconMarkup = spot.icon ? createIconMarkup(getIconFilename(spot.icon).replace(/\.svg$/, ''), 'spot-marker-icon') : '';

    const marker = L.marker([spot.coordinates.latitude, spot.coordinates.longitude], {
        icon: L.divIcon({
            className: `custom-spot-marker-container marker-style-${MARKER_STYLE.TYPE}`,
            html: (
                MARKER_STYLE.TYPE === 'circle'
                    ? `
                <div class="spot-marker-circle">
                    ${iconMarkup}
                </div>
            `
                    : `
                <div class="spot-marker-pin">
                    <div class="spot-marker-circle">
                        ${iconMarkup}
                    </div>
                </div>
            `
            ),
            iconSize: iconSize,
            iconAnchor: iconAnchor,
            popupAnchor: popupAnchor
        })
    });

    marker.options.spot = spot;
    marker.options.searchedTags = searchedTags;
    marker.options.matchType = matchType;
    if (!isCardsMode) {
        if (isImagePopupMode) {
            const popupContent = createImageOnlyPopup(spot);
            if (popupContent) {
                marker.bindPopup(popupContent, {
                    maxWidth: 200,
                    minWidth: 160,
                    className: 'marker-popup-image-container',
                    closeOnZoomAnimation: false, // Keep popup open during zoom to prevent flash
                    autoPan: false // Disable Leaflet's auto-pan; we handle this precisely on initial click
                });
            }
        } else {
            marker.bindPopup(createMarkerPopup(spot, searchedTags, matchType), {
                closeOnZoomAnimation: false, // Keep popup open during zoom to prevent flash
                autoPan: false // Disable Leaflet's auto-pan; we handle this precisely on initial click
            });
        }
    }

    // Always emit a spotSelected event with the full spot object on marker click (all modes)
    marker.on('click', async () => {
        // Mark as initial open for auto-panning
        marker._isInitialPopupOpen = true;

        // For visual focus in cards/image modes
        if (isCardsMode || isImagePopupMode) {
            const map = marker._map;
            if (map && spot && spot.id) {
                setFocusedMarker(spot.id, map);
            }
        }
        // For cards mode, also show cards banner
        if (isCardsMode) {
            const { focusSpotCards } = await import('../cards/cards-manager.js');
            focusSpotCards({ spot, searchedTags, matchType });
        }
        // For image popup mode, also show cards banner
        if (isImagePopupMode) {
            const { focusSpotCards } = await import('../cards/cards-manager.js');
            focusSpotCards({ spot, searchedTags, matchType });
        }
        // Always emit the event for map-controls.js and others
        window.dispatchEvent(new CustomEvent('spotSelected', { detail: { spot } }));
    });

    // Add hover tooltip functionality
    if (MARKER_HOVER_DISPLAY.title_hover || MARKER_HOVER_DISPLAY.id_hover) {
        let tooltipContent = '';
        if (MARKER_HOVER_DISPLAY.title_hover && spot.title) {
            tooltipContent += spot.title;
        }
        if (MARKER_HOVER_DISPLAY.id_hover && spot.id) {
            if (tooltipContent) tooltipContent += ' | ';
            tooltipContent += `ID: ${spot.id}`;
        }
        if (tooltipContent) {
            marker.bindTooltip(tooltipContent, {
                permanent: false,
                direction: 'top',
                className: 'marker-hover-tooltip',
                offset: [0, markerStyle.popupAnchor[1] * scale] // Use the same offset as popup
            });
        }
    }

    // Prevent tooltips from opening while any popup is open
    marker.on('tooltipopen', function () {
        if (isAnyPopupOpen) {
            marker.closeTooltip();
        }
    });

    // Set marker icon color and size based on matchType and spot.size
    function setMarkerColorAndSize() {
        const iconEl = marker._icon;
        if (iconEl) {
            // Apply per-spot size to container if needed
            if (spotSize !== 1.0) {
                iconEl.style.width = `${markerStyle.width * finalScale}px`;
                iconEl.style.height = `${markerStyle.height * finalScale}px`;

                // For pin style, also resize the pin container
                const pinEl = iconEl.querySelector('.spot-marker-pin');
                if (pinEl) {
                    pinEl.style.width = `${markerStyle.width * finalScale}px`;
                    pinEl.style.height = `${markerStyle.height * finalScale}px`;
                }
            }

            const circleEl = iconEl.querySelector('.spot-marker-circle');
            if (circleEl) {
                // Use spot.color if available, otherwise fallback to default (grey)
                let color = spot.color || (
                    matchType === 'exact'
                        ? TAG_SEARCH_SETTINGS.COLOR_EXACT_MATCH
                        : TAG_SEARCH_SETTINGS.COLOR_ONE_MATCH
                );
                circleEl.style.backgroundColor = color;

                // Apply per-spot size via inline styles (overrides CSS variables)
                if (spotSize !== 1.0) {
                    circleEl.style.width = `${markerStyle.circle * finalScale}px`;
                    circleEl.style.height = `${markerStyle.circle * finalScale}px`;
                    const iconImg = circleEl.querySelector('.spot-marker-icon');
                    if (iconImg) {
                        iconImg.style.width = `${markerStyle.icon * finalScale}px`;
                        iconImg.style.height = `${markerStyle.icon * finalScale}px`;
                    }
                }
            }
        }
    }

    // Set accessible name (aria-label) for screen readers
    function setMarkerAccessibleName() {
        const iconEl = marker._icon;
        if (iconEl) {
            // Get spot title/name using same logic as popup
            const lang = getCurrentLanguage();
            const langSuffix = lang === 'es' ? 'es' : lang === 'de' ? 'de' : 'en';
            let title = spot.title || '';
            if (!title) {
                const nameField = `name_${langSuffix}`;
                title = spot[nameField] || spot.name_en || '';
            }
            // Set aria-label with spot name, fallback to "Spot" if no name
            const accessibleName = title || `Spot ${spot.id || ''}`.trim() || 'Map marker';
            iconEl.setAttribute('aria-label', accessibleName);
            iconEl.setAttribute('role', 'button'); // Markers are clickable/interactive
        }
    }

    marker.on('add', () => {
        setMarkerColorAndSize();
        setMarkerAccessibleName();
    });
    // Also set immediately in case marker is already on map
    setTimeout(() => {
        setMarkerColorAndSize();
        setMarkerAccessibleName();
    }, 0);

    // --- POPUPS_IMAGE Mode: Clear focus when popup closes ---
    if (isImagePopupMode) {
        marker.on('popupclose', function () {
            // Only clear focus if we're NOT actively zooming/panning
            // (zoom causes temporary popup close which should not clear focus)
            if (!isZooming) {
                const map = marker._map;
                if (map) {
                    clearFocusedMarker();
                }
            } else {
                // Track that popup closed during zoom so we can reopen it
                popupClosedDuringZoom = true;
                console.log(`[MARKER] Popup closed during zoom, will reopen after`);
            }
        });
    }

    // --- Ensure popup is always visible (not covered by header/search bar or cards banner) ---
    if (!isCardsMode) {
        marker.on('popupopen', function () {
            // Track this marker for auto-reopen after zoom
            activePopupMarker = marker;

            // Hide hover tooltip when popup opens
            if (marker.getTooltip()) {
                marker.getTooltip()._container.style.display = 'none';
            }
            // Disable all marker hovers globally while a popup is open
            isAnyPopupOpen = true;
            const map = marker._map;
            if (map) hideAllMarkerHoverTooltips(map);

            const mapInstance = marker._map;
            if (!mapInstance) return;
            const popup = marker.getPopup();
            if (!popup) return;

            // Use multiple attempts to ensure popup is positioned correctly
            // First attempt after short delay, then verify and adjust if needed
            const adjustPopupPosition = () => {
                // ONLY pan if this is the initial opening of the popup
                // (Prevents annoying re-panning when user manually moves map or zooms)
                if (!marker._isInitialPopupOpen) {
                    if (window.LOGGING && window.LOGGING.ENABLED) {
                        console.log(`[MARKER] Skipping auto-pan for spot ${spot.id} (not initial open)`);
                    }
                    return;
                }

                const mapContainer = mapInstance.getContainer();
                const popupEl = popup._container;
                if (!popupEl || !mapContainer) return;

                // Get viewport dimensions
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;

                // Get popup position relative to viewport
                const popupRect = popupEl.getBoundingClientRect();

                // Get actual header and banner heights dynamically
                const headerEl = document.querySelector('.header') || document.querySelector('header');
                const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 80;

                const cardsBannerEl = document.getElementById('spots-cards-banner');
                const bannerHeight = cardsBannerEl && cardsBannerEl.classList.contains('show')
                    ? cardsBannerEl.getBoundingClientRect().height
                    : 0;

                // Calculate marker height to ensure it's visible below popup
                const markerHeight = (markerStyle.height * scale) + Math.abs(popupAnchor[1]) + 20;

                // Safe margins
                const topMargin = headerHeight + 20;
                const bottomMargin = bannerHeight + markerHeight + 30;
                const sideMargin = 20;

                let panX = 0;
                let panY = 0;

                // Check edges for overflow
                if (popupRect.top < topMargin) {
                    panY = -(topMargin - popupRect.top);
                }

                const bottomSpace = viewportHeight - popupRect.bottom;
                if (bottomSpace < bottomMargin) {
                    panY = (bottomMargin - bottomSpace);
                }

                if (popupRect.left < sideMargin) {
                    panX = -(sideMargin - popupRect.left);
                }

                const rightSpace = viewportWidth - popupRect.right;
                if (rightSpace < sideMargin) {
                    panX = (sideMargin - rightSpace);
                }

                const mapSize = mapInstance.getSize();
                const clampedPanX = Math.max(-mapSize.x * 0.4, Math.min(mapSize.x * 0.4, panX));
                const clampedPanY = Math.max(-mapSize.y * 0.4, Math.min(mapSize.y * 0.4, panY));

                if (Math.abs(clampedPanX) > 10 || Math.abs(clampedPanY) > 10) {
                    mapInstance.panBy([clampedPanX, clampedPanY], {
                        animate: true,
                        duration: 0.25
                    });
                }
            };

            // First adjustment after popup renders
            const initialDelay = window.innerWidth <= 768 ? 100 : 50;
            setTimeout(adjustPopupPosition, initialDelay);

            // Second adjustment after animation completes
            setTimeout(() => {
                adjustPopupPosition();
                // After the second check, we've definitively finished the "opening" pan
                marker._isInitialPopupOpen = false;
            }, initialDelay + 300);
        });

        // Show hover tooltips again only when no popups are open
        marker.on('popupclose', function () {
            // Only clear active popup if not zooming (zoom will reopen it)
            if (!isZooming) {
                activePopupMarker = null;
            }

            const map = marker._map;
            if (!map) return;
            let anyOpen = false;
            map.eachLayer(layer => {
                if (layer.getPopup && layer.getPopup() && layer.getPopup().isOpen && layer.getPopup()._isOpen) {
                    anyOpen = true;
                }
            });
            if (!anyOpen) {
                isAnyPopupOpen = false;
                showAllMarkerHoverTooltips(map);
            }
        });
    }

    // Add marker to registry for efficient focused marker lookup
    markerRegistry.set(String(spot.id), marker);

    return marker;
}

// --- Language change: update open popups ---
if (typeof window !== 'undefined' && window.L && window.L.Popup) {
    window.addEventListener('languageChanged', () => {
        document.querySelectorAll('.leaflet-container').forEach(mapEl => {
            const map = mapEl._leaflet_map;
            if (map && map.eachLayer) {
                map.eachLayer(layer => {
                    if (layer.getPopup && layer.getPopup() && layer.options && layer.options.spot) {
                        const popup = layer.getPopup();
                        if (popup.isOpen && popup._isOpen) {
                            layer.setPopupContent(createMarkerPopup(layer.options.spot, layer.options.searchedTags, layer.options.matchType));
                        }
                    }
                });
            }
        });
    });
    // User location change: update open popups to show distance
    window.addEventListener('userLocationChanged', () => {
        document.querySelectorAll('.leaflet-container').forEach(mapEl => {
            const map = mapEl._leaflet_map;
            if (map && map.eachLayer) {
                map.eachLayer(layer => {
                    if (layer.getPopup && layer.getPopup() && layer.options && layer.options.spot) {
                        const popup = layer.getPopup();
                        if (popup.isOpen && popup._isOpen) {
                            layer.setPopupContent(createMarkerPopup(layer.options.spot, layer.options.searchedTags, layer.options.matchType));
                        }
                    }
                });
            }
        });
    });
}

export function initializeMarkers() {
    // No-op for now; add marker initialization logic here if needed in the future
} 